import { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData, ConvertPurchaseOrderVerify, ConvertPurchaseOrderToPurchase } from '../../../apiService';
import LoadingOverlay from '../../common/LoadingOverlay';
import { useSessionValidation } from '../../../utils/sessionValidation';
import {
  Add, Edit, Visibility, Delete, ShoppingCart, FilterList, Refresh, Warning, CheckCircle, Cancel, Close, ExpandMore, ExpandLess, CheckCircleOutline, Print
} from '@mui/icons-material';
import {
  Button, IconButton, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box, Collapse, Divider
} from '@mui/material';
import PurchaseOrderModal from './PurchaseOrderModal';
import './PurchaseOrderIndex.css';

const PurchaseOrderIndex = () => {
  // Session validation
  useSessionValidation();

  const { companyId, branchId, token, userId, permissions, companyName } = useAuth();

  //-----------------PERMISSION--------------------
  const hasCreateMasterPermission = permissions?.includes('Create Master');
  const hasEditMasterPermission = permissions?.includes('Edit Master');
  const hasDeleteMasterPermission = permissions?.includes('Delete Master');

  // Modal states
  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [selectedItem, setSelectedItem] = useState(null);

  // Data states
  const [purchaseOrders, setPurchaseOrders] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [items, setItems] = useState([]);

  // Inactive modal states
  const [inactiveModalOpen, setInactiveModalOpen] = useState(false);
  const [inactiveViewData, setInactiveViewData] = useState(null);
  const [inactiveViewDetails, setInactiveViewDetails] = useState([]);

  // Supplier drill-down modal states
  const [supplierModalOpen, setSupplierModalOpen] = useState(false);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [supplierPurchaseOrders, setSupplierPurchaseOrders] = useState([]);
  const [expandedPOId, setExpandedPOId] = useState(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterVerified, setFilterVerified] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Debounced search to prevent excessive filtering
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Default fromDate to first day of current month, toDate to today
  const formatDateToYYYYMMDD = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getDefaultFromDate = () => {
    const today = new Date();
    return formatDateToYYYYMMDD(new Date(today.getFullYear(), today.getMonth(), 1));
  };
  const getDefaultToDate = () => formatDateToYYYYMMDD(new Date());

  const [fromDate, setFromDate] = useState(getDefaultFromDate());
  const [toDate, setToDate] = useState(getDefaultToDate());

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Form states
  const [formData, setFormData] = useState({});
  const [orderDetails, setOrderDetails] = useState([]);

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Loading...');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation modal
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');

  // Activate confirmation modal
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');

  useEffect(() => {
    if (companyId && token) {
      fetchAllData();
    }
  }, [companyId, token]);


  // Fetch functions
  const fetchPurchaseOrders = async () => {
    try {
      // Fetch active purchase orders
      const activeResponse = await fetchData('PurchaseOrderApi', `/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeResponse.response_data || activeResponse || []).map(item => ({ ...item, IsActive: true }));

      // Fetch inactive purchase orders
      let inactiveData = [];
      try {
        const inactiveResponse = await fetchData('PurchaseOrderApi', `/InActivePurchaseOrderListDetails/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveResponse.response_data || inactiveResponse || []).map(item => ({ ...item, IsActive: false }));
      } catch (inactiveError) {
        console.error('Error fetching inactive purchase orders:', inactiveError);
      }

      // Combine both active and inactive
      setPurchaseOrders([...activeData, ...inactiveData]);
    } catch (error) {
      console.error('Error fetching purchase orders:', error);
      setPurchaseOrders([]);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const response = await fetchData('ClientAndVendorApi', `/VendorList/${companyId}`, token);
      setSuppliers(response.response_data || response || []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      setSuppliers([]);
    }
  };

  /**
   * Fetch items with optional search functionality - Optimized with debouncing
   * @param {string} inputValue - Optional search term to filter items by name
   */
  const fetchItems = useCallback(async (inputValue = '') => {
    try {
      // Construct API path with optional search parameter
      // If inputValue is provided, use the search API: /Item/{companyId}/{branchId}/{searchTerm}
      // Otherwise, use the default API: /Item/{companyId}
      const apiPath = inputValue
        ? `/${companyId}/${branchId}/${encodeURIComponent(inputValue)}`
        : `/${companyId}`;

      const response = await fetchData('ItemApi', apiPath, token);
      const newItems = response.response_data || response || [];

      // Only update if items actually changed to prevent unnecessary re-renders
      setItems(prevItems => {
        if (JSON.stringify(prevItems) !== JSON.stringify(newItems)) {
          return newItems;
        }
        return prevItems;
      });
    } catch (error) {
      console.error('Error fetching items:', error);
      setItems([]);
    }
  }, [companyId, branchId, token]);

  const fetchMaxBillNo = async () => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/MaxBillno/${companyId}/${branchId || 1}`, token);
      console.log('MaxBillNo API Response:', response);
      const data = response.response_data || response || {};
      console.log('MaxBillNo Data:', data);

      const billNumber = data.BillNumber || data.billNumber || 1;
      const billPrefix = data.BillPrefix || data.billPrefix || '';
      const finYear = data.FinYear || data.finYear || '';

      // Generate PurchaseOrderNo based on available data
      let purchaseOrderNo = '';
      if (billPrefix && finYear) {
        purchaseOrderNo = `${billPrefix}/${billNumber}/${finYear}`;
      } else if (finYear) {
        purchaseOrderNo = `${billNumber}/${finYear}`;
      } else if (billPrefix) {
        purchaseOrderNo = `${billPrefix}/${billNumber}`;
      } else {
        purchaseOrderNo = `${billNumber}`;
      }

      return { billNumber, billPrefix, finYear, purchaseOrderNo };
    } catch (error) {
      console.error('Error fetching max bill number:', error);
      return { billNumber: 1, billPrefix: '', finYear: '', purchaseOrderNo: '1' };
    }
  };

  // Freight + Packing GST state
  const [freightPackingGST, setFreightPackingGST] = useState({ freight: null, packing: null });

  // Freight + Packing GST
  const fetchFreightAndPackingGST = async () => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/AccountHeadFreightAndPacking/${companyId}/${branchId || 1}`, token);
      console.log('AccountHeadFreightAndPacking API Response:', response);
      const data = response.response_data || response || [];
      console.log('AccountHeadFreightAndPacking Data:', data);

      // Find Freight and Packing entries from the response
      let freightData = null;
      let packingData = null;

      if (Array.isArray(data)) {
        freightData = data.find(item => item.Name?.toLowerCase().includes('freight') || item.AccountHeadName?.toLowerCase().includes('freight'));
        packingData = data.find(item => item.Name?.toLowerCase().includes('packing') || item.AccountHeadName?.toLowerCase().includes('packing'));
      } else if (data) {
        // If it's an object with Freight and Packing properties
        freightData = data.Freight || data.FreightCharge || null;
        packingData = data.Packing || data.PackingCharge || null;
      }

      setFreightPackingGST({
        freight: freightData,
        packing: packingData
      });

      return { freight: freightData, packing: packingData };
    } catch (error) {
      console.error('Error fetching freight and packing GST:', error);
      return { freight: null, packing: null };
    }
  };


  const fetchAllData = useCallback(async () => {
    setLoading(true);
    setLoadingMessage('Fetching purchase orders...');
    await Promise.allSettled([fetchPurchaseOrders(), fetchSuppliers(), fetchItems(), fetchFreightAndPackingGST()]);
    setLoading(false);
  }, [fetchItems]);

  const fetchSinglePurchaseOrder = async (id) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/${id}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching purchase order details:', error);
      return null;
    }
  };

  // Fetch inactive PO details for view modal
  const fetchInactivePODetails = async (id) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/InActivePurchaseOrderListDetails/${id}/${branchId}`, token);
      return response.response_data || response;
    } catch (error) {
      console.error('Error fetching inactive purchase order details:', error);
      return null;
    }
  };

  // Activate confirmation handlers
  const handleOpenActivateModal = (id, poNo) => {
    setActivateItemId(id);
    setActivateItemNo(poNo);
    setActivateModalOpen(true);
  };

  const handleCloseActivateModal = () => {
    setActivateModalOpen(false);
    setActivateItemId(null);
    setActivateItemNo('');
  };

  // Activate inactive purchase order
  const handleActivate = async () => {
    if (!activateItemId) return;

    setLoading(true);
    handleCloseActivateModal();
    setLoadingMessage('Activating purchase order...');
    try {
      await activateData('PurchaseOrderApi', `${activateItemId}`, token);
      setSuccess('Purchase order activated successfully!');
      await fetchAllData();
    } catch (error) {
      console.error('Error activating record:', error);
      setError('Failed to activate purchase order.');
    }
    setLoading(false);
  };

  // Open inactive view modal
  const handleOpenInactiveModal = async (item) => {
    setLoading(true);
    setLoadingMessage('Fetching inactive purchase order details...');

    const freshData = await fetchInactivePODetails(item.Id);

    if (freshData) {
      const poData = freshData.PurchaseOrder || freshData;
      const details = freshData.PurchaseOrderDetails || freshData.Details || [];
      setInactiveViewData(poData);
      setInactiveViewDetails(details);
    } else {
      setInactiveViewData(item);
      setInactiveViewDetails([]);
    }

    setLoading(false);
    setInactiveModalOpen(true);
  };

  // Close inactive view modal
  const handleCloseInactiveModal = () => {
    setInactiveModalOpen(false);
    setInactiveViewData(null);
    setInactiveViewDetails([]);
  };

  // Fetch PO history for modal (when supplier is selected in create mode)
  const fetchPOHistoryForModal = async (supplierId) => {
    try {
      const response = await fetchData('PurchaseOrderApi', `/PurchaseOrderHistoryByMaster/${supplierId}/${branchId}`, token);
      const responseData = response.response_data || response || [];

      let purchaseOrdersList = [];
      let purchaseOrderDetailsList = [];

      if (Array.isArray(responseData) && responseData.length > 0) {
        const firstItem = responseData[0];
        purchaseOrdersList = firstItem.PurchaseOrder || firstItem.purchaseOrder || [];
        purchaseOrderDetailsList = firstItem.PurchaseOrderDetails || firstItem.purchaseOrderDetails || [];
      } else if (responseData.PurchaseOrder) {
        purchaseOrdersList = responseData.PurchaseOrder || [];
        purchaseOrderDetailsList = responseData.PurchaseOrderDetails || [];
      }

      // Map details to each purchase order
      const purchaseOrdersWithDetails = purchaseOrdersList.map(po => {
        const details = purchaseOrderDetailsList.filter(detail => detail.PurchaseOrderId === po.Id);
        return { ...po, PurchaseOrderDetails: details };
      });

      return purchaseOrdersWithDetails;
    } catch (error) {
      console.error('Error fetching PO history for modal:', error);
      return [];
    }
  };

  // Modal handlers
  const handleOpenModal = async (action, itemOrId = null) => {
    setModalAction(action);
    const itemId = typeof itemOrId === 'object' ? itemOrId?.Id : itemOrId;

    if ((action === 'view' || action === 'edit') && itemId) {
      setLoading(true);
      setLoadingMessage('Fetching purchase order details...');

      const freshData = await fetchSinglePurchaseOrder(itemId);

      if (freshData) {
        const poData = freshData.PurchaseOrder || freshData;
        const details = freshData.PurchaseOrderDetails || freshData.Details || [];

        // Get supplier name from MasterName or find from suppliers list
        const supplierName = poData.MasterName || suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId || (s.MasterId || s.Id) === poData.SupplierId)?.Name || '';

        // Get InvoiceType name from suppliers list
        const supplier = suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId || (s.MasterId || s.Id) === poData.SupplierId);
        const invoiceTypeName = poData.InvoiceTypeName || supplier?.InvoiceTypeName || supplier?.InvoiceType || '';

        setSelectedItem(poData);
        setFormData({
          Id: poData.Id,
          CompanyId: poData.CompanyId || companyId,
          BranchId: poData.BranchId || 0,
          SupplierId: poData.MasterId || poData.SupplierId,
          MasterName: supplierName,
          InvoiceTypeId: poData.InvoiceTypeId || 0,
          InvoiceTypeName: invoiceTypeName,
          PurchaseOrderNo: poData.PurchaseOrderNo,
          PurchaseOrderDate: poData.PurchaseOrderDate?.split('T')[0] || new Date().toISOString().split('T')[0],
          FinYear: poData.FinYear || '',
          Instruction1: poData.Instruction1 || '',
          Instruction2: poData.Instruction2 || '',
          Remarks: poData.Remarks || '',
          Amount: poData.Amount || poData.amount || 0,
          DiscountAmount: poData.DiscountAmount || poData.discountAmount || 0,
          TaxableAmount: poData.TaxableAmount || poData.taxableAmount || 0,
          CGSTAmount: poData.CGSTAmount || poData.cgstAmount || poData.CgstAmount || 0,
          SGSTAmount: poData.SGSTAmount || poData.sgstAmount || poData.SgstAmount || 0,
          IGSTAmount: poData.IGSTAmount || poData.igstAmount || poData.IgstAmount || 0,
          FreightAmount: poData.FreightAmount || poData.freightAmount || 0,
          FreightGSTRate: poData.FreightGSTRate || poData.freightGSTRate || 0,
          FreightGSTAmount: poData.FreightGSTAmount || poData.freightGSTAmount || 0,
          PackingAmount: poData.PackingAmount || poData.packingAmount || 0,
          PackingGSTRate: poData.PackingGSTRate || poData.packingGSTRate || 0,
          PackingGSTAmount: poData.PackingGSTAmount || poData.packingGSTAmount || 0,
          RoundOff: poData.RoundOff || poData.roundOff || 0,
          TotalAmount: poData.TotalAmount || poData.totalAmount || 0,
          IsConverted: poData.IsConverted || false,
          IsVerified: poData.IsVerified || false,
          IsActive: poData.IsActive ?? true,
          // Audit fields
          CreatedBy: poData.CreatedBy || null,
          CreatedByName: poData.CreatedByName || '',
          CreatedDate: poData.CreatedDate || poData.CreatedOn || poData.CreatedAt || '',
          ModifiedByName: poData.ModifiedByName || poData.ModifiedBy || poData.UpdatedBy || '',
          ModifiedDate: poData.ModifiedDate || poData.ModifiedOn || poData.UpdatedOn || poData.UpdatedAt || ''
        });

        // Log the API response to debug
        console.log('PO Data from API:', poData);
        console.log('Amount fields:', {
          Amount: poData.Amount,
          TaxableAmount: poData.TaxableAmount,
          CGSTAmount: poData.CGSTAmount,
          SGSTAmount: poData.SGSTAmount,
          TotalAmount: poData.TotalAmount
        });
        setOrderDetails(details.map(d => {
          // Get item name from ItemName or find from items list
          const itemName = d.ItemName || items.find(i => i.Id === d.ItemId)?.Name || '';
          return {
            Id: d.Id || 0,
            ItemId: d.ItemId,
            ItemName: itemName,
            HSNCode: d.HSNCode || '',
            GSTRate: d.GSTRate || 0,
            Quantity: d.Quantity || 0,
            Rate: d.Rate || 0,
            Amount: d.Amount || 0,
            DiscountPercentage: d.DiscountPercentage || 0,
            DiscountAmount: d.DiscountAmount || 0,
            TaxableAmount: d.TaxableAmount || 0,
            CGSTAmount: d.CGSTAmount || 0,
            SGSTAmount: d.SGSTAmount || 0,
            IGSTAmount: d.IGSTAmount || 0,
            TotalAmount: d.TotalAmount || 0,
            IsActive: d.IsActive ?? true,
            CreatedBy: d.CreatedBy || null,
            CreatedDate: d.CreatedDate || d.CreatedOn || null
          };
        }));
      } else {
        const item = typeof itemOrId === 'object' ? itemOrId : null;
        setSelectedItem(item);
        setFormData(item || {});
        setOrderDetails([]);
      }

      setLoading(false);
    } else {
      // Create mode - fetch max bill number and freight/packing GST
      setLoading(true);
      setLoadingMessage('Generating PO number...');

      const maxBillData = await fetchMaxBillNo();
      const gstData = await fetchFreightAndPackingGST();

      // Get GST rates from API data
      const freightGSTRate = gstData.freight?.GSTRate || gstData.freight?.GstRate || 0;
      const packingGSTRate = gstData.packing?.GSTRate || gstData.packing?.GstRate || 0;

      setSelectedItem(null);
      setFormData({
        CompanyId: companyId,
        BranchId: branchId || 1,
        SupplierId: '',
        InvoiceTypeId: 0,
        PONumber: maxBillData.billNumber,
        PurchaseOrderNo: maxBillData.purchaseOrderNo,
        PurchaseOrderDate: new Date().toISOString().split('T')[0],
        FinYear: maxBillData.finYear,
        Instruction1: '',
        Instruction2: '',
        Remarks: '',
        Amount: 0,
        DiscountAmount: 0,
        TaxableAmount: 0,
        CGSTAmount: 0,
        SGSTAmount: 0,
        IGSTAmount: 0,
        FreightAmount: 0,
        FreightGSTRate: freightGSTRate,
        FreightGSTAmount: 0,
        PackingAmount: 0,
        PackingGSTRate: packingGSTRate,
        PackingGSTAmount: 0,
        RoundOff: 0,
        TotalAmount: 0,
        IsConverted: false,
        IsVerified: false,
        IsActive: true
      });
      setOrderDetails([]);
      setLoading(false);
    }

    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setOpenModal(false);
    setModalAction('');
    setSelectedItem(null);
    setFormData({});
    setOrderDetails([]);
    window.location.reload();
  };


  // Save handler
  const handleSave = async () => {
    setLoading(true);
    setError('');

    try {
      const actionText = modalAction === 'create' ? 'Creating' : 'Updating';
      setLoadingMessage(`${actionText} purchase order...`);

      // Get selected supplier details for MasterId, MasterName and InvoiceTypeId
      const selectedSupplier = suppliers.find(s => (s.MasterId || s.Id) === parseInt(formData.SupplierId));
      const masterId = selectedSupplier?.MasterId || selectedSupplier?.Id || 0;
      const masterName = selectedSupplier?.Name || '';
      // Try multiple field names for InvoiceTypeId
      const invoiceTypeId = selectedSupplier?.InvoiceTypeId || selectedSupplier?.invoiceTypeId ||
        selectedSupplier?.InvoiceType || selectedSupplier?.invoiceType ||
        formData.InvoiceTypeId || 0;

      // Debug log to see supplier data
      console.log('Selected Supplier:', selectedSupplier);
      console.log('InvoiceTypeId:', invoiceTypeId);

      // Calculate total quantity from all order details
      const totalQuantity = orderDetails.reduce((sum, detail) => sum + (parseFloat(detail.Quantity) || 0), 0);

      const dataToSend = {
        PurchaseOrder: {
          Id: modalAction === 'edit' ? (formData.Id || 0) : 0,
          CompanyId: companyId,
          BranchId: formData.BranchId || 0,
          MasterId: masterId,
          MasterName: masterName,
          InvoiceTypeId: invoiceTypeId,
          PurchaseOrderNo: formData.PurchaseOrderNo || '',
          PurchaseOrderDate: formData.PurchaseOrderDate || new Date().toISOString().split('T')[0],
          FinYear: formData.FinYear || '',
          Instruction1: formData.Instruction1 || '',
          Instruction2: formData.Instruction2 || '',
          Remarks: formData.Remarks || '',
          TotalQuantity: totalQuantity,
          Amount: parseFloat(formData.Amount) || 0,
          DiscountAmount: parseFloat(formData.DiscountAmount) || 0,
          TaxableAmount: parseFloat(formData.TaxableAmount) || 0,
          CGSTAmount: parseFloat(formData.CGSTAmount) || 0,
          SGSTAmount: parseFloat(formData.SGSTAmount) || 0,
          IGSTAmount: parseFloat(formData.IGSTAmount) || 0,
          FreightAmount: parseFloat(formData.FreightAmount) || 0,
          FreightGSTRate: parseFloat(formData.FreightGSTRate) || 0,
          FreightGSTAmount: parseFloat(formData.FreightGSTAmount) || 0,
          PackingAmount: parseFloat(formData.PackingAmount) || 0,
          PackingGSTRate: parseFloat(formData.PackingGSTRate) || 0,
          PackingGSTAmount: parseFloat(formData.PackingGSTAmount) || 0,
          RoundOff: Math.round((parseFloat(formData.RoundOff) || 0) * 100) / 100,
          TotalAmount: parseFloat(formData.TotalAmount) || 0,
          IsConverted: formData.IsConverted || false,
          IsVerified: formData.IsVerified || false,
          IsActive: formData.IsActive ?? true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          CreatedDate: modalAction === 'edit' ? formData.CreatedDate : null,
          ModifiedBy: modalAction === 'edit' ? userId : null
        },
        PurchaseOrderDetails: orderDetails.map(detail => {
          // Get item name from items dropdown
          const selectedItem = items.find(i => i.Id === parseInt(detail.ItemId));
          const itemName = selectedItem?.Name || '';

          return {
            Id: modalAction === 'edit' ? (detail.Id || 0) : 0,
            CompanyId: companyId,
            BranchId: formData.BranchId || 0,
            PurchaseOrderId: modalAction === 'edit' ? formData.Id : 0,
            ItemId: parseInt(detail.ItemId) || 0,
            ItemName: itemName,
            HSNCode: detail.HSNCode || '',
            GSTRate: parseFloat(detail.GSTRate) || 0,
            Quantity: parseFloat(detail.Quantity) || 0,
            Rate: parseFloat(detail.Rate) || 0,
            Amount: parseFloat(detail.Amount) || 0,
            DiscountPercentage: parseFloat(detail.DiscountPercentage) || 0,
            DiscountAmount: parseFloat(detail.DiscountAmount) || 0,
            TaxableAmount: parseFloat(detail.TaxableAmount) || 0,
            CGSTAmount: parseFloat(detail.CGSTAmount) || 0,
            SGSTAmount: parseFloat(detail.SGSTAmount) || 0,
            IGSTAmount: parseFloat(detail.IGSTAmount) || 0,
            TotalAmount: parseFloat(detail.TotalAmount) || 0,
            IsActive: detail.IsActive ?? true,
            CreatedBy: modalAction === 'create' ? userId : detail.CreatedBy,
            CreatedDate: modalAction === 'edit' ? detail.CreatedDate : null,
            ModifiedBy: modalAction === 'edit' ? userId : null
          };
        })
      };

      if (modalAction === 'create') {
        await postData('PurchaseOrderApi', '', dataToSend, token);
        setSuccess('Purchase order created successfully!');
      } else if (modalAction === 'edit') {
        if (!formData.Id) throw new Error('Purchase Order ID is missing');
        await updateData('PurchaseOrderApi', formData.Id, dataToSend, token);
        setSuccess('Purchase order updated successfully!');
      }

      handleCloseModal();
      await fetchPurchaseOrders();
      setLoading(false);
    } catch (error) {
      console.error('Error saving:', error);
      setError(`Failed to ${modalAction} purchase order. Please try again.`);
      setLoading(false);
    }
  };

  // Verify handler
  const handleVerify = async (id) => {
    if (!id) return;

    setLoading(true);
    setLoadingMessage('Verifying purchase order...');
    try {
      await ConvertPurchaseOrderVerify('PurchaseOrderApi', id, token);
      setSuccess('Purchase order verified successfully!');
      handleCloseModal();
      await fetchPurchaseOrders();
    } catch (error) {
      console.error('Error verifying:', error);
      setError('Failed to verify purchase order.');
    }
    setLoading(false);
  };

  // Convert handler
  const handleConvert = async (id) => {
    if (!id) return;

    setLoading(true);
    setLoadingMessage('Converting to purchase...');
    try {
      await ConvertPurchaseOrderToPurchase('PurchaseOrderApi', id, token);
      setSuccess('Purchase order converted to purchase successfully!');
      handleCloseModal();
      await fetchPurchaseOrders();
    } catch (error) {
      console.error('Error converting to purchase:', error);
      setError('Failed to convert purchase order.');
    }
    setLoading(false);
  };

  // Optimized supplier click handler
  const handleSupplierClick = useCallback(async (supplierId, supplierName) => {
    setLoading(true);
    setLoadingMessage('Fetching supplier purchase orders...');
    try {
      const response = await fetchData('PurchaseOrderApi', `/PurchaseOrderHistoryByMaster/${supplierId}/${branchId}`, token);
      const responseData = response.response_data || response || [];

      // Handle the response structure - it's an array with PurchaseOrder and PurchaseOrderDetails
      let purchaseOrdersList = [];
      let purchaseOrderDetailsList = [];

      if (Array.isArray(responseData) && responseData.length > 0) {
        // Extract PurchaseOrder and PurchaseOrderDetails from the first item
        const firstItem = responseData[0];
        purchaseOrdersList = firstItem.PurchaseOrder || firstItem.purchaseOrder || [];
        purchaseOrderDetailsList = firstItem.PurchaseOrderDetails || firstItem.purchaseOrderDetails || [];
      } else if (responseData.PurchaseOrder) {
        purchaseOrdersList = responseData.PurchaseOrder || [];
        purchaseOrderDetailsList = responseData.PurchaseOrderDetails || [];
      }

      // Map details to each purchase order
      const purchaseOrdersWithDetails = purchaseOrdersList.map(po => {
        const details = purchaseOrderDetailsList.filter(detail => detail.PurchaseOrderId === po.Id);
        return { ...po, PurchaseOrderDetails: details };
      });

      setSelectedSupplier({ id: supplierId, name: supplierName });
      setSupplierPurchaseOrders(purchaseOrdersWithDetails);
      setExpandedPOId(null);
      setSupplierModalOpen(true);
    } catch (error) {
      console.error('Error fetching supplier purchase orders:', error);
      // Fallback: filter from existing data
      const filteredPOs = purchaseOrders.filter(po => po.MasterId === supplierId || po.SupplierId === supplierId);
      setSelectedSupplier({ id: supplierId, name: supplierName });
      setSupplierPurchaseOrders(filteredPOs);
      setExpandedPOId(null);
      setSupplierModalOpen(true);
    }
    setLoading(false);
  }, [branchId, token, purchaseOrders]);

  const handleCloseSupplierModal = () => {
    setSupplierModalOpen(false);
    setSelectedSupplier(null);
    setSupplierPurchaseOrders([]);
    setExpandedPOId(null);
  };

  const togglePOExpand = async (poId) => {
    if (expandedPOId === poId) {
      setExpandedPOId(null);
    } else {
      setExpandedPOId(poId);
    }
  };

  // Delete handlers
  const handleOpenDeleteModal = (id, poNo) => {
    setDeleteItemId(id);
    setDeleteItemNo(poNo);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeleteItemId(null);
    setDeleteItemNo('');
  };

  const handleDelete = async () => {
    if (!deleteItemId) return;

    setLoading(true);
    handleCloseDeleteModal();
    try {
      await deleteData('PurchaseOrderApi', deleteItemId, token);
      setSuccess('Purchase order deleted successfully!');
      await fetchPurchaseOrders();
    } catch (error) {
      console.error('Error deleting:', error);
      setError('Failed to delete purchase order.');
    }
    setLoading(false);
  };

  // Filter and pagination - Memoized for performance
  const getFilteredData = useCallback((data) => {
    if (!Array.isArray(data)) return [];
    let filtered = data;

    if (debouncedSearchTerm) {
      const searchLower = debouncedSearchTerm.toLowerCase();
      filtered = filtered.filter(item =>
        item.PurchaseOrderNo?.toLowerCase().includes(searchLower) ||
        item.MasterName?.toLowerCase().includes(searchLower) ||
        item.SupplierName?.toLowerCase().includes(searchLower)
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item =>
        filterStatus === 'active' ? item.IsActive : !item.IsActive
      );
    }

    if (filterVerified !== 'all') {
      filtered = filtered.filter(item =>
        filterVerified === 'verified' ? item.IsVerified : !item.IsVerified
      );
    }

    // Date range filter
    if (fromDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.PurchaseOrderDate?.split('T')[0];
        return itemDate >= fromDate;
      });
    }

    if (toDate) {
      filtered = filtered.filter(item => {
        const itemDate = item.PurchaseOrderDate?.split('T')[0];
        return itemDate <= toDate;
      });
    }

    return filtered;
  }, [debouncedSearchTerm, filterStatus, filterVerified, fromDate, toDate]);

  // Memoized pagination data
  const paginationData = useMemo(() => {
    const filtered = getFilteredData(purchaseOrders) || [];
    const startIndex = (currentPage - 1) * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return {
      data: Array.isArray(filtered) ? filtered.slice(startIndex, endIndex) : [],
      totalCount: Array.isArray(filtered) ? filtered.length : 0,
      totalPages: Math.ceil((Array.isArray(filtered) ? filtered.length : 0) / rowsPerPage),
      startIndex
    };
  }, [purchaseOrders, getFilteredData, currentPage, rowsPerPage]);

  // Memoized supplier lookup
  const supplierLookup = useMemo(() => {
    const lookup = {};
    suppliers.forEach(supplier => {
      const id = supplier.MasterId || supplier.Id;
      if (id) {
        lookup[id] = supplier;
      }
    });
    return lookup;
  }, [suppliers]);

  // Memoized items lookup
  const itemsLookup = useMemo(() => {
    const lookup = {};
    items.forEach(item => {
      if (item.Id) {
        lookup[item.Id] = item;
      }
    });
    return lookup;
  }, [items]);

  const handlePageChange = useCallback((page) => setCurrentPage(page), []);
  const handleRowsPerPageChange = useCallback((e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  }, []);
  const handleRefresh = useCallback(() => fetchAllData(), []);
  const handleCloseSnackbar = useCallback(() => {
    setError('');
    setSuccess('');
  }, []);

  // Print individual Purchase Order handler - Professional Format
  const handlePrintPO = async (poId) => {
    setLoading(true);
    setLoadingMessage('Preparing print...');

    try {
      const freshData = await fetchSinglePurchaseOrder(poId);

      if (!freshData) {
        setError('Failed to fetch purchase order details for printing.');
        setLoading(false);
        return;
      }

      const poData = freshData.PurchaseOrder || freshData;
      const details = freshData.PurchaseOrderDetails || freshData.Details || [];

      // Get supplier details
      const supplier = suppliers.find(s => (s.MasterId || s.Id) === poData.MasterId);
      const supplierName = poData.MasterName || supplier?.Name || '-';
      const supplierAddress = supplier?.Address || poData.SupplierAddress || '';
      const supplierGST = supplier?.GSTNumber || poData.SupplierGSTNumber || '';
      const supplierContact = supplier?.ContactNumber || supplier?.Mobile || '';
      const supplierEmail = supplier?.Email || '';

      // Company name from auth context  
      const companyNameDisplay = companyName || 'Company Name';

      const printWindow = window.open('', '_blank');

      // Build item rows
      let itemRows = '';
      details.forEach((d, idx) => {
        const itemData = items.find(i => i.Id === d.ItemId);
        const itemName = d.ItemName || itemData?.Name || '-';
        const uom = d.UOM || itemData?.UOM || '-';
        const taxableAmt = parseFloat(d.TaxableAmount) || 0;
        const gstAmt = (parseFloat(d.CGSTAmount) || 0) + (parseFloat(d.SGSTAmount) || 0) + (parseFloat(d.IGSTAmount) || 0);
        const isLast = idx === details.length - 1;
        const borderBottom = isLast ? 'border-bottom:1px solid #000;' : '';

        itemRows += '<tr style="height:22px;">' +
          '<td style="border-left:1px solid #000;border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:center;font-size:9px;">' + (idx + 1) + '</td>' +
          '<td style="border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:left;font-size:9px;">' + (d.HSNCode || '-') + '</td>' +
          '<td style="border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:left;font-size:9px;">' + itemName + '</td>' +
          '<td style="border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:center;font-size:9px;">' + (d.Quantity || '') + '</td>' +
          '<td style="border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:center;font-size:9px;">' + uom + '</td>' +
          '<td style="border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:right;font-size:9px;">' + (d.Rate || '') + '</td>' +
          '<td style="border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:right;font-size:9px;">' + (d.DiscountPercentage || '') + '</td>' +
          '<td style="border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:right;font-size:9px;">' + (taxableAmt ? taxableAmt.toFixed(2) : '') + '</td>' +
          '<td style="border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:right;font-size:9px;">' + (d.GSTRate || '') + '</td>' +
          '<td style="border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:right;font-size:9px;">' + (gstAmt ? gstAmt.toFixed(2) : '') + '</td>' +
          '<td style="border-right:1px solid #000;' + borderBottom + 'padding:3px 5px;text-align:right;font-size:9px;">' + (d.TotalAmount ? d.TotalAmount.toFixed(2) : '') + '</td>' +
          '</tr>';
      });

      const printContent = '<!DOCTYPE html><html><head><title>PO-' + poData.PurchaseOrderNo + '</title>' +
        '<style>*{margin:0;padding:0;box-sizing:border-box}body{font-family:Arial,sans-serif;font-size:10px}table{width:100%;border-collapse:collapse}h6{font-size:10px;font-weight:normal}' +
        '@media print{.no-print{display:none!important}@page{margin:5mm;size:A4}}</style>' +
        '</head><body style="padding:10px;width:94%;margin:10px auto;">' +

        // Action bar
        '<div class="no-print" style="position:fixed;top:0;left:0;right:0;background:#1e40af;padding:8px 15px;display:flex;justify-content:space-between;align-items:center;z-index:999;">' +
        '<span style="color:white;font-size:13px;font-weight:600;">Purchase Order: ' + poData.PurchaseOrderNo + '</span>' +
        '<div><button onclick="window.close()" style="padding:6px 18px;margin-right:10px;border:1px solid rgba(255,255,255,0.4);border-radius:4px;background:transparent;color:white;cursor:pointer;font-size:12px;">Cancel</button>' +
        '<button onclick="window.print()" style="padding:6px 18px;border:none;border-radius:4px;background:white;color:#1e40af;cursor:pointer;font-size:12px;font-weight:600;">Print</button></div></div>' +

        '<div style="margin-top:45px;">' +

        // Company Header
        '<div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:5px;">' +
        '<div style="font-size:14px;font-weight:bold;color:#1e40af;">' + companyNameDisplay + '</div>' +
        '<div style="text-align:right;font-size:9px;line-height:1.4;"></div></div>' +

        // PO Title
        '<h5 style="text-align:center;margin:5px 0;padding:5px;border:1px solid black;font-size:12px;font-weight:bold;">PURCHASE ORDER</h5>' +

        // Supplier & PO Info
        '<table><tr>' +
        '<td style="border:1px solid #000;width:62%;vertical-align:top;padding:8px;">' +
        '<h6 style="margin-bottom:5px;">To</h6>' +
        '<h6 style="margin-left:15px;font-weight:bold;">' + supplierName + '</h6>' +
        (supplierAddress ? '<h6 style="margin-left:15px;font-size:9px;">' + supplierAddress + '</h6>' : '') +
        '<br/>' +
        (supplierGST ? '<h6 style="margin-left:15px;font-size:9px;">GST NO: ' + supplierGST + '</h6>' : '') +
        (supplierContact ? '<h6 style="margin-left:15px;font-size:9px;">TEL: ' + supplierContact + '</h6>' : '') +
        (supplierEmail ? '<h6 style="margin-left:15px;font-size:9px;">Email: ' + supplierEmail + '</h6>' : '') +
        '</td>' +
        '<td style="border:1px solid #000;width:38%;vertical-align:top;padding:8px;">' +
        '<h6 style="margin:3px 0;">PO No <span style="margin-left:12px;">:</span> ' + poData.PurchaseOrderNo + '</h6>' +
        '<h6 style="margin:3px 0;">Date <span style="margin-left:18px;">:</span> ' + formatDate(poData.PurchaseOrderDate) + '</h6>' +
        '<h6 style="margin:3px 0;">FY <span style="margin-left:30px;">:</span> ' + (poData.FinYear || '-') + '</h6>' +
        '</td></tr></table>' +

        // Terms
        '<table><tr><td style="border:1px solid #000;padding:5px;font-size:8px;line-height:1.5;">' +
        '<p style="margin:2px 10px;">* We are pleased to place an order for the supply of following materials subject to Terms & Conditions.</p>' +
        '<p style="margin:2px 10px;">* Material supplied must conform to approved specifications / samples.</p>' +
        '<p style="margin:2px 10px;">* P.O. No & Item Code must appear on invoice.</p>' +
        '<p style="margin:2px 10px;">* GST Number should be clearly mentioned on the invoice.</p>' +
        '</td></tr></table>' +

        // Items Table
        '<table><thead><tr>' +
        '<td style="border:1px solid #000;width:4%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">Sl.No</td>' +
        '<td style="border:1px solid #000;width:7%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">HSN</td>' +
        '<td style="border:1px solid #000;width:24%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">Item / Description</td>' +
        '<td style="border:1px solid #000;width:5%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">QTY</td>' +
        '<td style="border:1px solid #000;width:5%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">UOM</td>' +
        '<td style="border:1px solid #000;width:7%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">Rate</td>' +
        '<td style="border:1px solid #000;width:5%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">Disc%</td>' +
        '<td style="border:1px solid #000;width:10%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">Taxable Amt</td>' +
        '<td style="border:1px solid #000;width:5%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">GST%</td>' +
        '<td style="border:1px solid #000;width:9%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">GST Amt</td>' +
        '<td style="border:1px solid #000;width:10%;padding:4px;text-align:center;font-size:9px;font-weight:bold;">Total</td>' +
        '</tr></thead><tbody>' + itemRows + '</tbody></table>' +

        // Special Instructions & Total
        '<table><tr><td colspan="11" style="border:1px solid black;padding:5px;">' +
        '<div style="display:flex;justify-content:space-between;">' +
        '<h6>SPECIAL INSTRUCTIONS</h6>' +
        '<span style="font-weight:bold;font-size:11px;">Total Value Rs. ' + (poData.TotalAmount || 0).toFixed(2) + '</span>' +
        '</div></td></tr>' +

        // Payment & Statutory
        '<tr><td colspan="11" style="border:1px solid black;padding:5px;">' +
        '<div style="display:flex;justify-content:space-between;">' +
        '<div style="font-size:9px;">' +
        '<div style="margin:2px 10px;">Payment Terms : As per mutually accepted terms.</div>' +
        '<div style="margin:2px 10px;">Freight : TO PAY</div>' +
        (poData.FreightAmount > 0 ? '<div style="margin:2px 10px;">Pkg/Frw Charges : ' + (poData.FreightAmount || 0).toFixed(2) + '</div>' : '') +
        (poData.Instruction1 ? '<div style="margin:2px 10px;">Instruction : ' + poData.Instruction1 + '</div>' : '') +
        (poData.Instruction2 ? '<div style="margin:2px 10px;">Instruction 2 : ' + poData.Instruction2 + '</div>' : '') +
        (poData.Remarks ? '<div style="margin:2px 10px;">Remarks : ' + poData.Remarks + '</div>' : '') +
        '</div>' +
        '<div style="text-align:right;font-size:9px;"><strong>STATUTORY DETAILS</strong></div>' +
        '</div></td></tr>' +

        // Delivery Address
        '<tr><td colspan="11" style="border:1px solid black;padding:5px;">' +
        '<h6>Delivery Address</h6>' +
        '<div style="margin-left:10px;font-size:9px;">' + companyNameDisplay + '</div>' +
        '</td></tr>' +

        // Signatures
        '<tr>' +
        '<td colspan="5" style="border:1px solid black;padding:20px 5px 5px;text-align:center;font-size:9px;">(CHECKED BY)</td>' +
        '<td colspan="6" style="border:1px solid black;padding:20px 5px 5px;text-align:center;font-size:9px;">(AUTHORIZED SIGNATORY)</td>' +
        '</tr></table>' +

        '</div></body></html>';

      if (printWindow) {
        printWindow.document.write(printContent);
        printWindow.document.close();
      } else {
        setError('Popup blocked. Please allow popups for this site.');
      }

    } catch (error) {
      console.error('Error printing PO:', error);
      setError('Failed to print purchase order.');
    }

    setLoading(false);
  };

  // Memoized table row component for better performance
  const PurchaseOrderRow = useMemo(() => {
    return ({ item, index, startIndex, supplierLookup, handleOpenModal, handleSupplierClick, handleOpenDeleteModal, handlePrintPO, handleOpenInactiveModal, handleOpenActivateModal }) => {
      const supplier = supplierLookup[item.MasterId || item.SupplierId];
      const supplierName = item.MasterName || supplier?.Name || '-';

      return (
        <tr key={item.Id} style={{ backgroundColor: !item.IsActive ? '#fef2f2' : 'inherit' }}>
          <td>
            {item.IsActive ? (
              <span onClick={() => handleOpenModal('edit', item)} style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 600, textDecoration: 'underline' }}>
                {startIndex + index + 1}
              </span>
            ) : (
              <span style={{ fontWeight: 500, color: '#6b7280' }}>{startIndex + index + 1}</span>
            )}
          </td>
          <td>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <ShoppingCart sx={{ fontSize: 18, color: item.IsActive ? '#1e40af' : '#6b7280' }} />
              {item.PurchaseOrderNo || '-'}
            </div>
          </td>
          <td>{formatDate(item.PurchaseOrderDate)}</td>
          <td>
            {item.IsActive ? (
              <span
                onClick={() => handleSupplierClick(item.MasterId || item.SupplierId, supplierName)}
                style={{ color: '#1e40af', cursor: 'pointer', fontWeight: 500, textDecoration: 'underline' }}
              >
                {supplierName}
              </span>
            ) : (
              <span style={{ color: '#6b7280' }}>{supplierName}</span>
            )}
          </td>
          <td style={{ fontWeight: 600 }}>{formatCurrency(item.TotalAmount)}</td>
          <td>
            {item.IsVerified ? (
              <Chip icon={<CheckCircle sx={{ fontSize: 14 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
            ) : (
              <Chip icon={<Cancel sx={{ fontSize: 14 }} />} label="Pending" color="warning" size="small" sx={{ fontSize: '0.65rem', height: '22px' }} />
            )}
          </td>
          <td>
            {item.IsConverted ? (
              <Chip label="Converted" color="info" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
            ) : (
              <Chip label="Open" color="default" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
            )}
          </td>
          <td>
            {item.IsActive ? (
              <Chip label="Active" color="success" size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
            ) : (
              <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fee2e2', color: '#dc2626' }} />
            )}
          </td>
          <td style={{ textAlign: 'right' }}>
            <div className="action-buttons">
              {item.IsActive ? (
                /* Active item actions: View, Edit, Delete, Print */
                <>
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => handleOpenModal('view', item.Id)}>
                      <Visibility style={{ color: 'white' }} />
                    </IconButton>
                  </Tooltip>
                  {hasEditMasterPermission && (
                    <Tooltip title="Edit">
                      <IconButton size="small" onClick={() => handleOpenModal('edit', item.Id)} disabled={item.IsVerified || item.IsConverted}>
                        <Edit style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {hasDeleteMasterPermission && (
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => handleOpenDeleteModal(item.Id, item.PurchaseOrderNo)} disabled={item.IsVerified || item.IsConverted}>
                        <Delete style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                  {item.IsVerified && (
                    <Tooltip title="Print">
                      <IconButton size="small" onClick={() => handlePrintPO(item.Id)} className="print-action-btn">
                        <Print style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              ) : (
                /* Inactive item actions: View, Activate */
                <>
                  <Tooltip title="View">
                    <IconButton size="small" onClick={() => handleOpenInactiveModal(item)}>
                      <Visibility style={{ color: 'white' }} />
                    </IconButton>
                  </Tooltip>
                  {hasEditMasterPermission && (
                    <Tooltip title="Activate">
                      <IconButton size="small" onClick={() => handleOpenActivateModal(item.Id, item.PurchaseOrderNo)} sx={{ bgcolor: '#10b981', '&:hover': { bgcolor: '#059669' } }}>
                        <CheckCircleOutline style={{ color: 'white' }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </>
              )}
            </div>
          </td>
        </tr>
      );
    };
  }, [hasEditMasterPermission, hasDeleteMasterPermission]);

  // Format helpers - memoized
  const formatDate = useCallback((dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
  }, []);

  const formatCurrency = useCallback((amount) => {
    return `₹${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }, []);

  const { data: paginatedData, totalCount, totalPages, startIndex } = paginationData;


  return (
    <div className="purchase-order-dashboard">
      <div className="dashboard-header">
        <h5 className="plain-header">Purchase Order Management</h5>
      </div>

      <div className="dashboard-content">
        <div className="data-table-container">
          <div className="table-header">
            <div className={`search-filter-section ${showMobileFilters ? 'mobile-filters-visible' : ''}`}>
              {/* Mobile Filter Toggle Button */}
              <IconButton
                className="filter-toggle-btn mobile-only"
                onClick={() => setShowMobileFilters(!showMobileFilters)}
                title="Show Filters"
              >
                <FilterList />
              </IconButton>

              <div className="search-box">
                <input
                  type="text"
                  placeholder="Search by PO No, Supplier..."
                  value={searchTerm}
                  onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                />
              </div>
              <div className="date-filter">
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }}
                  title="From Date"
                />
              </div>
              <div className="date-filter">
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }}
                  title="To Date"
                />
              </div>
              <div className="filter-section">
                <FilterList className="filter-icon" />
                <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All Status</option>
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
              <div className="filter-section">
                <select value={filterVerified} onChange={(e) => { setFilterVerified(e.target.value); setCurrentPage(1); }}>
                  <option value="all">All</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
              {/* Desktop buttons */}
              <Button variant="outlined" onClick={handleRefresh} disabled={loading} className="refresh-button desktop-only" startIcon={<Refresh />}>
                Refresh
              </Button>
              <Button variant="contained" startIcon={<Add />} onClick={() => handleOpenModal('create')} className="add-button desktop-only">
                Add Purchase Order
              </Button>
              {/* Mobile icon buttons */}
              <Tooltip title="Refresh">
                <IconButton onClick={handleRefresh} disabled={loading} className="refresh-icon-btn mobile-only">
                  <Refresh />
                </IconButton>
              </Tooltip>
              <Tooltip title="Add Purchase Order">
                <IconButton onClick={() => handleOpenModal('create')} className="add-icon-btn mobile-only">
                  <Add />
                </IconButton>
              </Tooltip>
            </div>
          </div>

          {/* Table View - Single table for both Active and Inactive */}
          <div className="table-wrapper">
            <table className="data-table">
              <thead>
                <tr>
                  <th>Sl No.</th>
                  <th>PO No.</th>
                  <th>Date</th>
                  <th>Supplier</th>
                  <th>Total Amount</th>
                  <th>Verified</th>
                  <th>Converted</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((item, index) => (
                  <PurchaseOrderRow
                    key={item.Id}
                    item={item}
                    index={index}
                    startIndex={startIndex}
                    supplierLookup={supplierLookup}
                    handleOpenModal={handleOpenModal}
                    handleSupplierClick={handleSupplierClick}
                    handleOpenDeleteModal={handleOpenDeleteModal}
                    handlePrintPO={handlePrintPO}
                    handleOpenInactiveModal={handleOpenInactiveModal}
                    handleOpenActivateModal={handleOpenActivateModal}
                  />
                ))}
                {paginatedData.length === 0 && (
                  <tr>
                    <td colSpan="9" style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
                      No purchase orders found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="pagination-section">
            <div className="pagination-info">
              <span>Showing {totalCount > 0 ? startIndex + 1 : 0} to {Math.min(startIndex + rowsPerPage, totalCount)} of {totalCount} entries</span>
              <div className="rows-per-page">
                <span>Rows per page:</span>
                <select value={rowsPerPage} onChange={handleRowsPerPageChange}>
                  <option value={5}>5</option>
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>
            </div>
            <div className="pagination-controls">
              <button className="pagination-btn" onClick={() => handlePageChange(1)} disabled={currentPage === 1}>First</button>
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1}>Prev</button>
              {[...Array(totalPages)].map((_, i) => {
                const page = i + 1;
                if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                  return (
                    <button key={page} className={`pagination-btn ${currentPage === page ? 'active' : ''}`} onClick={() => handlePageChange(page)}>
                      {page}
                    </button>
                  );
                } else if (page === currentPage - 2 || page === currentPage + 2) {
                  return <span key={page} className="pagination-ellipsis">...</span>;
                }
                return null;
              })}
              <button className="pagination-btn" onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages || totalPages === 0}>Next</button>
              <button className="pagination-btn" onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages || totalPages === 0}>Last</button>
            </div>
          </div>
        </div>
      </div>

      <LoadingOverlay open={loading} message={loadingMessage} />

      <PurchaseOrderModal
        open={openModal}
        onClose={handleCloseModal}
        modalAction={modalAction}
        selectedItem={selectedItem}
        formData={formData}
        setFormData={setFormData}
        orderDetails={orderDetails}
        setOrderDetails={setOrderDetails}
        onSave={handleSave}
        onVerify={handleVerify}
        onConvert={handleConvert}
        suppliers={suppliers}
        items={items}
        loading={loading}
        freightPackingGST={freightPackingGST}
        onFetchPOHistory={fetchPOHistoryForModal}
        onFetchItems={fetchItems}
      />

      <Snackbar open={!!success} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="success">{success}</Alert>
      </Snackbar>

      <Snackbar open={!!error} autoHideDuration={6000} onClose={handleCloseSnackbar} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <Alert onClose={handleCloseSnackbar} severity="error">{error}</Alert>
      </Snackbar>

      {/* Delete Confirmation Modal */}
      <Dialog open={deleteModalOpen} onClose={handleCloseDeleteModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#fef2f2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#dc2626', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Delete Purchase Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to delete purchase order <strong style={{ color: '#1e293b' }}>{deleteItemNo}</strong>? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseDeleteModal} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } }}>
            Cancel
          </Button>
          <Button onClick={handleDelete} variant="contained" sx={{ backgroundColor: '#dc2626', '&:hover': { backgroundColor: '#b91c1c' } }}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Activate Confirmation Modal */}
      <Dialog open={activateModalOpen} onClose={handleCloseActivateModal} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', padding: '8px' } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ backgroundColor: '#f0fdf4', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircleOutline sx={{ color: '#10b981', fontSize: 24 }} />
            </Box>
            <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b' }}>Activate Purchase Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ color: '#64748b', fontSize: '0.9rem' }}>
            Are you sure you want to activate purchase order <strong style={{ color: '#1e293b' }}>{activateItemNo}</strong>? This will restore the purchase order to active status.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
          <Button onClick={handleCloseActivateModal} variant="outlined" sx={{ borderColor: '#e2e8f0', color: '#64748b', '&:hover': { borderColor: '#cbd5e1', backgroundColor: '#f8fafc' } }}>
            Cancel
          </Button>
          <Button onClick={handleActivate} variant="contained" sx={{ backgroundColor: '#10b981', '&:hover': { backgroundColor: '#059669' } }}>
            Activate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Supplier Purchase Orders Modal */}
      <Dialog
        open={supplierModalOpen}
        onClose={handleCloseSupplierModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#f8fafc' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#1e40af', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                Purchase Orders - {selectedSupplier?.name}
              </Typography>
              <Chip
                label={`${supplierPurchaseOrders.length} Orders`}
                color="primary"
                size="small"
                sx={{ fontSize: '0.7rem', height: '22px' }}
              />
            </Box>
            <IconButton onClick={handleCloseSupplierModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          <Box sx={{ maxHeight: '65vh', overflow: 'auto' }}>
            {supplierPurchaseOrders.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 4, color: '#6b7280' }}>
                <Typography>No purchase orders found for this supplier</Typography>
              </Box>
            ) : (
              supplierPurchaseOrders.map((po, poIndex) => (
                <Box key={po.Id} sx={{ borderBottom: '1px solid #e2e8f0' }}>
                  {/* PO Header Row - Clickable */}
                  <Box
                    onClick={() => togglePOExpand(po.Id)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      p: 1.5,
                      cursor: 'pointer',
                      bgcolor: expandedPOId === po.Id ? '#f0f9ff' : (poIndex % 2 === 0 ? '#fff' : '#f9fafb'),
                      '&:hover': { bgcolor: '#f0f9ff' }
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {expandedPOId === po.Id ? <ExpandLess sx={{ color: '#1e40af' }} /> : <ExpandMore sx={{ color: '#64748b' }} />}
                        <Typography sx={{ fontWeight: 600, color: '#1e40af', fontSize: '0.9rem' }}>
                          {po.PurchaseOrderNo}
                        </Typography>
                      </Box>
                      <Typography sx={{ color: '#64748b', fontSize: '0.8rem' }}>
                        {formatDate(po.PurchaseOrderDate)}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        {po.IsVerified ? (
                          <Chip icon={<CheckCircle sx={{ fontSize: 12 }} />} label="Verified" color="success" size="small" sx={{ fontSize: '0.6rem', height: '20px' }} />
                        ) : (
                          <Chip label="Pending" color="warning" size="small" sx={{ fontSize: '0.6rem', height: '20px' }} />
                        )}
                        {po.IsConverted && (
                          <Chip label="Converted" color="info" size="small" sx={{ fontSize: '0.6rem', height: '20px' }} />
                        )}
                      </Box>
                    </Box>
                    <Typography sx={{ fontWeight: 700, color: '#1e40af', fontSize: '0.95rem' }}>
                      {formatCurrency(po.TotalAmount)}
                    </Typography>
                  </Box>

                  {/* PO Details - Expandable */}
                  <Collapse in={expandedPOId === po.Id}>
                    <Box sx={{ bgcolor: '#f8fafc', p: 1.5 }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: '#374151', mb: 1, display: 'block' }}>
                        Order Details ({po.PurchaseOrderDetails?.length || 0} items)
                      </Typography>
                      <Box sx={{ overflow: 'auto' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                          <thead>
                            <tr style={{ background: '#e2e8f0' }}>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>#</th>
                              <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>Item</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>HSN</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>Qty</th>
                              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Rate</th>
                              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Amount</th>
                              <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>GST%</th>
                              <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#374151' }}>Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(po.PurchaseOrderDetails || []).map((detail, detailIndex) => (
                              <tr key={detail.Id || detailIndex} style={{ borderBottom: '1px solid #e2e8f0', background: detailIndex % 2 === 0 ? '#fff' : '#f9fafb' }}>
                                <td style={{ padding: '8px', textAlign: 'left' }}>{detailIndex + 1}</td>
                                <td style={{ padding: '8px', textAlign: 'left', fontWeight: 500 }}>{detail.ItemName || '-'}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{detail.HSNCode || '-'}</td>
                                <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{detail.Quantity || 0}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.Rate)}</td>
                                <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.Amount)}</td>
                                <td style={{ padding: '8px', textAlign: 'center' }}>{detail.GSTRate || 0}%</td>
                                <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>{formatCurrency(detail.TotalAmount)}</td>
                              </tr>
                            ))}
                            {(!po.PurchaseOrderDetails || po.PurchaseOrderDetails.length === 0) && (
                              <tr>
                                <td colSpan="8" style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No details available</td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </Box>
                      {/* Summary Row */}
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 3, mt: 1.5, pt: 1, borderTop: '1px solid #e2e8f0' }}>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Taxable: <strong style={{ color: '#374151' }}>{formatCurrency(po.TaxableAmount)}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          GST: <strong style={{ color: '#166534' }}>{formatCurrency((po.CGSTAmount || 0) + (po.SGSTAmount || 0) + (po.IGSTAmount || 0))}</strong>
                        </Typography>
                        <Typography variant="caption" sx={{ color: '#64748b' }}>
                          Total: <strong style={{ color: '#1e40af' }}>{formatCurrency(po.TotalAmount)}</strong>
                        </Typography>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              ))
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#f8fafc', justifyContent: 'space-between' }}>
          <Typography variant="caption" sx={{ color: '#64748b' }}>
            Total Orders: <strong>{supplierPurchaseOrders.length}</strong> |
            Total Amount: <strong style={{ color: '#1e40af' }}>{formatCurrency(supplierPurchaseOrders.reduce((sum, po) => sum + (po.TotalAmount || 0), 0))}</strong>
          </Typography>
          <Button onClick={handleCloseSupplierModal} variant="outlined" size="small" sx={{ borderColor: '#d1d5db', color: '#374151' }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Inactive Purchase Order View Modal */}
      <Dialog
        open={inactiveModalOpen}
        onClose={handleCloseInactiveModal}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: '12px', maxHeight: '85vh' } }}
      >
        <DialogTitle sx={{ py: 1.5, px: 2, borderBottom: '1px solid #e2e8f0', bgcolor: '#fef2f2' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ShoppingCart sx={{ color: '#dc2626', fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600, color: '#1e293b', fontSize: '1rem' }}>
                View Inactive Purchase Order
              </Typography>
              <Chip label="Inactive" size="small" sx={{ fontSize: '0.65rem', height: '20px', bgcolor: '#fee2e2', color: '#dc2626' }} />
            </Box>
            <IconButton onClick={handleCloseInactiveModal} size="small" sx={{ p: 0.5 }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 2 }}>
          {inactiveViewData && (
            <>
              {/* Order Information */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Order Information</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2, p: 1.5, bgcolor: '#f9fafb', borderRadius: 1 }}>
                <Box sx={{ minWidth: 120 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>PO Number</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{inactiveViewData.PurchaseOrderNo || '-'}</Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Date</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{formatDate(inactiveViewData.PurchaseOrderDate)}</Typography>
                </Box>
                <Box sx={{ minWidth: 150 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Supplier</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>{inactiveViewData.MasterName || '-'}</Typography>
                </Box>
                <Box sx={{ minWidth: 100 }}>
                  <Typography variant="caption" sx={{ color: '#6b7280', display: 'block' }}>Total Amount</Typography>
                  <Typography variant="body2" sx={{ fontWeight: 700, color: '#1e40af' }}>{formatCurrency(inactiveViewData.TotalAmount)}</Typography>
                </Box>
              </Box>

              {/* Order Items */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Order Items ({inactiveViewDetails.length} items)</Typography>
              <Box sx={{ border: '1px solid #e2e8f0', borderRadius: 1, overflow: 'hidden', mb: 2 }}>
                <Box sx={{ maxHeight: 250, overflow: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
                    <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                      <tr style={{ background: '#fee2e2' }}>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>#</th>
                        <th style={{ padding: '8px', textAlign: 'left', fontWeight: 600, color: '#991b1b' }}>Item</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>HSN</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>Qty</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Rate</th>
                        <th style={{ padding: '8px', textAlign: 'center', fontWeight: 600, color: '#991b1b' }}>GST%</th>
                        <th style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#991b1b' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {inactiveViewDetails.map((detail, index) => (
                        <tr key={detail.Id || index} style={{ borderBottom: '1px solid #e2e8f0', background: index % 2 === 0 ? '#fff' : '#f9fafb' }}>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{index + 1}</td>
                          <td style={{ padding: '8px', fontWeight: 500 }}>{detail.ItemName || items.find(i => i.Id === detail.ItemId)?.Name || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{detail.HSNCode || '-'}</td>
                          <td style={{ padding: '8px', textAlign: 'center', fontWeight: 600 }}>{detail.Quantity || 0}</td>
                          <td style={{ padding: '8px', textAlign: 'right' }}>{formatCurrency(detail.Rate)}</td>
                          <td style={{ padding: '8px', textAlign: 'center' }}>{detail.GSTRate || 0}%</td>
                          <td style={{ padding: '8px', textAlign: 'right', fontWeight: 600, color: '#1e40af' }}>{formatCurrency(detail.TotalAmount)}</td>
                        </tr>
                      ))}
                      {inactiveViewDetails.length === 0 && (
                        <tr>
                          <td colSpan="7" style={{ padding: '16px', textAlign: 'center', color: '#6b7280' }}>No items found</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </Box>
              </Box>

              {/* Order Summary */}
              <Typography variant="caption" sx={{ fontWeight: 600, color: '#dc2626', mb: 1, display: 'block' }}>Order Summary</Typography>
              <Box sx={{ bgcolor: '#fef2f2', borderRadius: 1, p: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Sub Total:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(inactiveViewData.Amount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Discount:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: '#dc2626' }}>- {formatCurrency(inactiveViewData.DiscountAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">Taxable:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>{formatCurrency(inactiveViewData.TaxableAmount)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" color="text.secondary">GST:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 500, color: '#166534' }}>{formatCurrency((inactiveViewData.CGSTAmount || 0) + (inactiveViewData.SGSTAmount || 0) + (inactiveViewData.IGSTAmount || 0))}</Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5 }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626' }}>Grand Total:</Typography>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: '#dc2626', fontSize: '0.9rem' }}>{formatCurrency(inactiveViewData.TotalAmount)}</Typography>
                </Box>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 2, py: 1.5, borderTop: '1px solid #e2e8f0', bgcolor: '#fef2f2' }}>
          <Button onClick={handleCloseInactiveModal} variant="outlined" size="small" sx={{ borderColor: '#dc2626', color: '#dc2626', '&:hover': { borderColor: '#b91c1c', bgcolor: '#fee2e2' } }}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default PurchaseOrderIndex;
